<script setup>
// O detalhe de uma campanha: quem recebeu, quem não, e por quê.
//
// Existe porque a lista respondia "quantos" e nunca "quem". Uma campanha que sai com "1 enviada"
// e não chega em ninguém é indistinguível, no card, de uma que funcionou — e o motivo da recusa
// mora em cada mensagem (`external_error`), não no lote.
//
// É aqui também que o progresso vive. Ele saiu do wizard, onde nascia no fim de uma página longa
// e fora da viewport; quem dispara chega nesta tela e vê os números subirem.
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import outboundsAPI from 'dashboard/api/outbounds';
import { audienceLabel } from 'dashboard/helper/campaignAudience';
import { messageStamp } from 'shared/helpers/timeHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();
const accountId = store.getters.getCurrentAccountId;

const destinatarios = ref([]);
const carregando = ref(true);
const progresso = ref(null);
const acaoEmCurso = ref('');
let timer = null;

const labels = useMapGetter('labels/getLabels');

// A lista já está no store: entrar pelo card não deve custar outra busca da campanha inteira.
const campanha = computed(() =>
  getters['campaigns/getAllCampaigns'].value.find(
    c => String(c.id) === String(route.params.campaignId)
  )
);

const idReal = computed(() => progresso.value?.campaign_id || null);
const audiencia = computed(() =>
  audienceLabel(campanha.value?.audience, labels.value)
);

const recusadas = computed(() =>
  destinatarios.value.filter(d => d.status === 'failed')
);
const entregues = computed(() =>
  destinatarios.value.filter(d => ['delivered', 'read'].includes(d.status))
);
const semConfirmacao = computed(() =>
  destinatarios.value.filter(d => ['sent', 'progress'].includes(d.status))
);

const emAndamento = computed(
  () => Boolean(progresso.value) && !progresso.value.concluido
);

const corDoStatus = status =>
  ({
    delivered: 'text-n-teal-11',
    read: 'text-n-teal-11',
    failed: 'text-n-ruby-11',
  })[status] || 'text-n-slate-11';

async function carregarDestinatarios() {
  try {
    const dados = await outboundsAPI.campaignRecipients(
      accountId,
      route.params.campaignId
    );
    destinatarios.value = dados?.recipients || [];
  } catch (error) {
    destinatarios.value = [];
  } finally {
    carregando.value = false;
  }
}

// O progresso só é perguntado enquanto há lote andando: parar de perguntar quando conclui é o
// que impede a tela de bater no servidor a cada 3s para sempre.
async function acompanhar() {
  try {
    progresso.value = await outboundsAPI.campaignProgress(
      accountId,
      route.params.campaignId
    );
  } catch (error) {
    pararDeAcompanhar();
    return;
  }
  if (progresso.value?.concluido) {
    pararDeAcompanhar();
    carregarDestinatarios();
    return;
  }
  timer = setTimeout(acompanhar, 3000);
}

function pararDeAcompanhar() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

async function reenviarRecusadas() {
  acaoEmCurso.value = 'retry';
  try {
    const r = await outboundsAPI.campaignRetryFailed(
      accountId,
      route.params.campaignId
    );
    useAlert(t('CAMPAIGN.WHATSAPP.DETAIL.RETRY_OK', { count: r?.count ?? 0 }));
    store.dispatch('campaigns/get');
    router.push({ name: 'campaigns_index', params: { accountId } });
  } catch (error) {
    useAlert(error?.message || t('CAMPAIGN.WHATSAPP.DETAIL.ACTION_ERROR'));
  } finally {
    acaoEmCurso.value = '';
  }
}

async function repetir() {
  acaoEmCurso.value = 'repeat';
  try {
    await outboundsAPI.campaignRepeat(accountId, route.params.campaignId);
    useAlert(t('CAMPAIGN.WHATSAPP.DETAIL.REPEAT_OK'));
    store.dispatch('campaigns/get');
    router.push({ name: 'campaigns_index', params: { accountId } });
  } catch (error) {
    useAlert(error?.message || t('CAMPAIGN.WHATSAPP.DETAIL.ACTION_ERROR'));
  } finally {
    acaoEmCurso.value = '';
  }
}

function voltar() {
  router.push({ name: 'campaigns_index', params: { accountId } });
}

onMounted(() => {
  if (!getters['campaigns/getAllCampaigns'].value.length) {
    store.dispatch('campaigns/get');
  }
  carregarDestinatarios();
  acompanhar();
});
onBeforeUnmount(pararDeAcompanhar);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <div class="flex items-center min-w-0 gap-3">
            <Button
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-arrow-left"
              @click="voltar"
            />
            <span class="truncate text-heading-1 text-n-slate-12">
              {{ campanha?.title || t('CAMPAIGN.WHATSAPP.DETAIL.TITLE') }}
            </span>
          </div>
          <div class="flex gap-2 shrink-0">
            <Button
              v-if="recusadas.length"
              variant="faded"
              color="ruby"
              size="sm"
              :is-loading="acaoEmCurso === 'retry'"
              :disabled="Boolean(acaoEmCurso)"
              :label="
                t('CAMPAIGN.WHATSAPP.DETAIL.RETRY', { count: recusadas.length })
              "
              @click="reenviarRecusadas"
            />
            <Button
              size="sm"
              variant="faded"
              color="slate"
              icon="i-lucide-repeat"
              :is-loading="acaoEmCurso === 'repeat'"
              :disabled="Boolean(acaoEmCurso)"
              :label="t('CAMPAIGN.WHATSAPP.DETAIL.REPEAT')"
              @click="repetir"
            />
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="flex flex-col w-full max-w-5xl gap-4 py-4 mx-auto">
        <!-- Progresso ao vivo. Os números vêm de `campaign_dispatches`, que conta o que de fato
             saiu — e não do que foi entregue ao backend, que marcaria 100% antes da primeira
             mensagem. Some quando conclui, porque aí quem responde é a tabela abaixo. -->
        <div
          v-if="emAndamento"
          class="p-4 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex items-center justify-between mb-2">
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.DETAIL.RUNNING') }}
            </p>
            <p class="text-xs tabular-nums text-n-slate-11">
              {{ progresso.enviados }} / {{ progresso.total || '—' }}
            </p>
          </div>
          <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
            <div
              class="h-full transition-all duration-500 rounded-full bg-n-brand"
              :style="{
                width: progresso.total
                  ? `${Math.round((progresso.enviados / progresso.total) * 100)}%`
                  : '0%',
              }"
            />
          </div>
        </div>

        <div
          class="flex flex-wrap gap-6 px-4 py-3 border rounded-xl border-n-weak"
        >
          <div v-if="entregues.length" class="flex items-baseline gap-1.5">
            <span class="text-lg font-semibold text-n-teal-11 tabular-nums">
              {{ entregues.length }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.DETAIL.DELIVERED') }}
            </span>
          </div>
          <div v-if="semConfirmacao.length" class="flex items-baseline gap-1.5">
            <span class="text-lg font-semibold text-n-slate-12 tabular-nums">
              {{ semConfirmacao.length }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.DETAIL.AWAITING') }}
            </span>
          </div>
          <div v-if="recusadas.length" class="flex items-baseline gap-1.5">
            <span class="text-lg font-semibold text-n-ruby-11 tabular-nums">
              {{ recusadas.length }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.DETAIL.REJECTED') }}
            </span>
          </div>
          <div v-if="audiencia" class="flex items-baseline gap-1.5">
            <span class="text-sm text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.CARD.CAMPAIGN_DETAILS.AUDIENCE') }}
            </span>
            <span class="text-sm font-medium text-n-slate-12">
              {{ audiencia }}
            </span>
          </div>
        </div>

        <div
          v-if="carregando"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>

        <div
          v-else-if="destinatarios.length"
          class="overflow-x-auto border rounded-xl border-n-weak"
        >
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left bg-n-alpha-1 text-n-slate-11">
                <th class="px-4 py-2 font-medium">
                  {{ t('CAMPAIGN.WHATSAPP.DETAIL.COL_NAME') }}
                </th>
                <th class="px-4 py-2 font-medium">
                  {{ t('CAMPAIGN.WHATSAPP.DETAIL.COL_PHONE') }}
                </th>
                <th class="px-4 py-2 font-medium">
                  {{ t('CAMPAIGN.WHATSAPP.DETAIL.COL_STATUS') }}
                </th>
                <th class="px-4 py-2 font-medium">
                  {{ t('CAMPAIGN.WHATSAPP.DETAIL.COL_WHEN') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="d in destinatarios"
                :key="d.message_id"
                class="border-t border-n-weak"
              >
                <td class="px-4 py-2 text-n-slate-12">{{ d.name || '—' }}</td>
                <td class="px-4 py-2 text-n-slate-11">{{ d.phone || '—' }}</td>
                <td class="px-4 py-2">
                  <span :class="corDoStatus(d.status)">
                    {{ t(`CAMPAIGN.WHATSAPP.DETAIL.STATUS.${d.status}`) }}
                  </span>
                  <!-- O motivo da recusa, que é o que diz se dá para consertar e reenviar. -->
                  <span v-if="d.error" class="block text-xs text-n-ruby-11">
                    {{ d.error }}
                  </span>
                </td>
                <td class="px-4 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ d.sent_at ? messageStamp(d.sent_at, 'dd/MM HH:mm') : '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-else class="py-10 text-sm text-center text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.DETAIL.EMPTY') }}
        </p>
      </div>
    </main>
  </section>
</template>
